import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import {
  Phone, Users, Link2, Zap,
  Mail, MessageSquare, AlertTriangle, CheckCircle2,
  Settings, ChevronRight, ToggleLeft, ToggleRight,
  Ticket, Calendar, CreditCard, Bot, X, Clock, Bell, FileText, Activity, XCircle, MinusCircle
} from 'lucide-react';

import { BACKEND_URL } from "../lib/backendUrl";
const API = BACKEND_URL + '/api';

const Logo = ({ linkTo }) => {
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate(linkTo)} className="flex items-center gap-2" data-testid="logo">
      <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center">
        <span className="text-white font-bold text-sm">M</span>
      </div>
      <span className="text-white font-semibold text-lg">Miadesk</span>
    </button>
  );
};

const TRIGGER_ICONS = {
  mail: Mail,
  message: MessageSquare,
  ticket: Ticket,
  clock: Clock,
  bell: Bell,
  phone: Phone,
  chat: MessageSquare,
  file: FileText,
  check: CheckCircle2,
};

const INTEGRATION_ICONS = {
  twilio: Phone,
  calendly: Calendar,
  stripe: CreditCard,
  sendgrid: Mail,
  openai: Bot,
};

const IntegrationsWorkflows = () => {
  const [activeTab, setActiveTab] = useState('workflows');
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'workflows') setActiveTab('workflows');
  }, [searchParams]);
  const [workflows, setWorkflows] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingWf, setEditingWf] = useState(null);
  const [editConfig, setEditConfig] = useState({});
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchWorkflows = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/workflows/`, { headers });
      setWorkflows(res.data);
    } catch { /* */ }
  }, [token]);

  const fetchActivity = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/workflows/activity`, { headers });
      setActivity(res.data.runs || []);
    } catch { /* */ }
  }, [token]);

  useEffect(() => {
    Promise.all([fetchWorkflows(), fetchActivity()]).finally(() => setLoading(false));
  }, []);

  const toggleWorkflow = async (wfId, currentEnabled) => {
    try {
      await axios.put(`${API}/workflows/${wfId}`, { enabled: !currentEnabled }, { headers });
      toast({ title: !currentEnabled ? 'Workflow enabled' : 'Workflow disabled' });
      fetchWorkflows();
    } catch {
      toast({ title: 'Failed to update', variant: 'destructive' });
    }
  };

  const saveWorkflowConfig = async (wfId) => {
    try {
      await axios.put(`${API}/workflows/${wfId}`, { config: editConfig }, { headers });
      toast({ title: 'Workflow updated' });
      setEditingWf(null);
      fetchWorkflows();
    } catch {
      toast({ title: 'Failed to save', variant: 'destructive' });
    }
  };

  const tabs = [
    { id: 'calls', label: 'Calls', icon: Phone },
    { id: 'leads', label: 'Leads', icon: Users },
    { id: 'workflows', label: 'Workflows', icon: Zap },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1f35] to-[#0a1628] flex items-center justify-center">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1f35] to-[#0a1628]" data-testid="integrations-page">
      <header className="border-b border-white/5 bg-[#0a1628]/80 backdrop-blur-lg">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-4">
          <Logo linkTo="/dashboard" />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex items-center gap-1 mb-8 border-b border-white/10" data-testid="iw-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                if (tab.id === 'calls') navigate('/call-history');
                else if (tab.id === 'leads') navigate('/crm');
                else setActiveTab(tab.id);
              }}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-all ${
                activeTab === tab.id ? 'border-blue-500 text-white' : 'border-transparent text-gray-500 hover:text-gray-300'
              }`}
              data-testid={`iw-tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>

        {/* Workflows Tab */}
        {activeTab === 'workflows' && (
          <div className="space-y-6" data-testid="workflows-content">
            <div className="text-center mb-8">
              <h2 className="text-xl font-semibold text-white">Automations</h2>
              <p className="text-gray-400 text-sm mt-1">Automate tasks when events happen in Miadesk</p>
            </div>
            <div className="space-y-4">
              {workflows.map(wf => {
                const Icon = TRIGGER_ICONS[wf.icon] || Zap;
                return (
                  <Card key={wf.id} className={`border transition-all ${wf.enabled ? 'bg-gray-900/50 border-blue-500/30' : 'bg-gray-900/30 border-white/10'}`} data-testid={`workflow-${wf.id}`}>
                    <CardContent className="pt-5">
                      <div className="flex items-center gap-4">
                        <div className={`w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 ${wf.enabled ? 'bg-blue-500/15' : 'bg-gray-500/10'}`}>
                          <Icon className={`w-5 h-5 ${wf.enabled ? 'text-blue-400' : 'text-gray-500'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="text-white font-medium text-sm">{wf.name}</h3>
                            {wf.enabled && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 border border-blue-500/20">Active</span>}
                          </div>
                          <p className="text-gray-400 text-xs mt-0.5">{wf.description}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20">{wf.trigger_label}</span>
                            <ChevronRight className="w-3 h-3 text-gray-600" />
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">{wf.action_label}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => { setEditingWf(editingWf === wf.id ? null : wf.id); setEditConfig(wf.config || {}); }}
                            className="text-gray-400 hover:text-white text-xs"
                            data-testid={`edit-wf-${wf.id}`}
                          >
                            <Settings className="w-3.5 h-3.5" />
                          </Button>
                          <button
                            onClick={() => toggleWorkflow(wf.id, wf.enabled)}
                            className="focus:outline-none"
                            data-testid={`toggle-wf-${wf.id}`}
                          >
                            {wf.enabled ? (
                              <ToggleRight className="w-8 h-8 text-blue-500" />
                            ) : (
                              <ToggleLeft className="w-8 h-8 text-gray-600" />
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Edit config panel */}
                      {editingWf === wf.id && (
                        <div className="mt-4 pt-4 border-t border-white/10 space-y-3" data-testid={`wf-config-${wf.id}`}>
                          {Object.entries(editConfig).map(([key, val]) => (
                            <div key={key}>
                              <Label className="text-gray-400 text-xs capitalize">{key.replace(/_/g, ' ')}</Label>
                              {String(val).length > 60 ? (
                                <textarea
                                  value={val}
                                  onChange={e => setEditConfig(c => ({ ...c, [key]: e.target.value }))}
                                  className="w-full bg-black/30 border border-white/10 rounded-md text-white text-xs p-2 mt-1 h-24 resize-none focus:border-blue-500/50 outline-none"
                                  data-testid={`wf-field-${key}`}
                                />
                              ) : (
                                <Input
                                  value={val}
                                  onChange={e => setEditConfig(c => ({ ...c, [key]: e.target.value }))}
                                  className="bg-black/30 border-white/10 text-white h-8 text-xs mt-1"
                                  data-testid={`wf-field-${key}`}
                                />
                              )}
                              <p className="text-gray-600 text-[10px] mt-0.5">
                                Available: {'{name}'}, {'{email}'}, {'{phone}'}, {'{business_name}'}, {'{date_time}'}, {'{caller_number}'}
                              </p>
                            </div>
                          ))}
                          <div className="flex gap-2 justify-end">
                            <Button size="sm" variant="outline" onClick={() => setEditingWf(null)} className="border-white/10 text-gray-400 text-xs">
                              <X className="w-3 h-3 mr-1" /> Cancel
                            </Button>
                            <Button size="sm" onClick={() => saveWorkflowConfig(wf.id)} className="bg-blue-600 hover:bg-blue-700 text-white text-xs" data-testid={`save-wf-${wf.id}`}>
                              Save Configuration
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Recent Activity */}
            <div className="pt-8" data-testid="workflow-activity-section">
              <div className="flex items-center gap-2 mb-4">
                <Activity className="w-4 h-4 text-blue-400" />
                <h3 className="text-white font-semibold text-sm">Recent Activity</h3>
                <span className="text-gray-500 text-xs">— every time a workflow fires, it shows up here</span>
              </div>
              {activity.length === 0 ? (
                <Card className="bg-gray-900/30 border-white/10">
                  <CardContent className="py-8 text-center">
                    <p className="text-gray-500 text-sm" data-testid="workflow-activity-empty">No workflow runs yet. Activity will appear here when events happen (new leads, bookings, calls, invoices...).</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {activity.map(run => (
                    <div key={run.id} className="flex items-center gap-3 bg-gray-900/40 border border-white/5 rounded-lg px-4 py-2.5" data-testid={`workflow-run-${run.id}`}>
                      {run.status === 'success' ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                      ) : run.status === 'failed' ? (
                        <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                      ) : (
                        <MinusCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-white text-xs font-medium">{run.workflow_name}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                            run.status === 'success' ? 'bg-emerald-500/10 text-emerald-400' :
                            run.status === 'failed' ? 'bg-red-500/10 text-red-400' : 'bg-amber-500/10 text-amber-400'
                          }`}>{run.status}</span>
                        </div>
                        <p className="text-gray-500 text-[11px] truncate mt-0.5">{run.detail}</p>
                      </div>
                      <span className="text-gray-600 text-[10px] flex-shrink-0">
                        {run.created_at ? new Date(run.created_at).toLocaleString() : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default IntegrationsWorkflows;
